import React, { useState, useEffect, useRef } from "react";
import Staple from "@/assets/icons/Staple.svg";
import { useLocation } from "react-router-dom";
import { showCustomToast } from "@/components/CustomToast";
import { API_URL } from "@/env";
import { useIntegrationsStore } from "@/hooks/useIntegrations";
import IntegrationsList from "./IntegrationsList";
import { AnimatePresence } from "motion/react";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import JiraSm from "@/assets/icons/JiraSm.svg";
import SlackSm from "@/assets/icons/SlackSm.svg";
import LinearSm from "@/assets/icons/LinearSm.svg";
import JiraIssueModal from "./JiraIssueModal";
import LinearIssueModal from "./LinearIssueModal";
import ToolbarLogo from "@/assets/icons/ToolbarLogo.svg";
import IntegrationsIcons from "@/assets/icons/IntegrationsIcons.svg";
import ChevronDownSm from "@/assets/icons/ChevronDownSm.svg";
import useReportsStore from "@/hooks/useReports";
import { useAICommentsStore } from "@/hooks/useAIComments";

const ReportHeader = ({
  title,
  reportId,
}: {
  title: string;
  reportId: string | null;
}) => {
  const location = useLocation();
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showIntegrationsDropdown, setShowIntegrationsDropdown] =
    useState(false);
  const [showJiraModal, setShowJiraModal] = useState(false);
  const [showLinearModal, setShowLinearModal] = useState(false);
  const [editableTitle, setEditableTitle] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);
  const integrationsRef = useRef<HTMLInputElement>(null);
  const { integrations, selectedIntegration, setSelectedIntegration } = useIntegrationsStore();
  const { updateReport } = useReportsStore();
  const { getReproductionSteps } = useAICommentsStore();

  // Get reproduction steps from the global store
  const reproductionSteps = getReproductionSteps();

  useEffect(() => {
    setEditableTitle(title);
  }, [title]);

  // Automatically set selected integration when one becomes connected
  useEffect(() => {
    if (!selectedIntegration) {
      if (integrations.jira.connected) {
        setSelectedIntegration("jira");
      } else if (integrations.linear.connected) {
        setSelectedIntegration("linear");
      } else if (integrations.slack.connected) {
        setSelectedIntegration("slack");
      }
    }
  }, [integrations.jira.connected, integrations.linear.connected, integrations.slack.connected, selectedIntegration, setSelectedIntegration]);

  useOutsideClick(integrationsRef, () => {
    setShowIntegrationsDropdown(false);
  });

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleTitleClick = () => {
    setIsEditing(true);
  };

  const handleTitleBlur = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsEditing(false);
    if (!e.target.value.trim()) {
      setEditableTitle("Untitled");
    }

    if (e.target.value === title) {
      return;
    }

    const response = await fetch(`${API_URL}/reports/${reportId}/update`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        title: editableTitle,
      }),
    });

    const data = await response.json();
    if (data.id) {
      showCustomToast("Report title updated", "check");
      // Update the report in the store so the search works immediately
      updateReport(reportId as string, { title: editableTitle });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setIsEditing(false);
      inputRef.current?.blur();
    }
    if (e.key === "Escape") {
      setEditableTitle(title); // Revert to original
      setIsEditing(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className="flex justify-between items-center pb-[12px] gap-[10px]">
      <div
        onClick={() => window.scrollTo(0, 0)}
        className="flex items-center gap-4 leading-[17.85px] onest-md min-w-0 overflow-hidden w-full"
      >
        <a href="/" className="shrink-0">
          <img src={ToolbarLogo} />
        </a>
        <p className="shrink-0 text-[14px] text-white/[0.25] cursor-default">
          /
        </p>
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editableTitle}
            onChange={(e) => setEditableTitle(e.target.value)}
            onBlur={handleTitleBlur}
            onKeyDown={handleKeyDown}
            className="truncate min-w-0 w-full py-[9.5px] text-[14px] bg-transparent border-none outline-none"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <p
            onClick={(e) => {
              e.stopPropagation();
              handleTitleClick();
            }}
            className="truncate min-w-0 py-[9.5px] text-[14px] cursor-text"
          >
            {editableTitle}
          </p>
        )}
      </div>
      <div className="shrink-0 flex gap-[8px]">
        <div className="relative z-[11] hidden lg:flex" ref={integrationsRef}>
          <button
            onClick={() => {
              if (selectedIntegration === "jira" && integrations.jira.connected) {
                setShowJiraModal(true);
              } else if (selectedIntegration === "linear" && integrations.linear.connected) {
                setShowLinearModal(true);
              } else if (selectedIntegration === "slack" && integrations.slack.connected) {
                // Handle Slack action here when implemented
              } else {
                setShowIntegrationsDropdown((prev) => !prev);
              }
            }}
            className={`flex pl-[10px] pr-[11px] gap-1.5 items-center justify-center font-medium h-[36px] text-[14px] leading-[20px] bg-gray-200 transition-all hover:bg-gray-300 ${
              integrations.jira.connected || integrations.linear.connected || integrations.slack.connected ? "border-r-[1px] hover:border-white/[0.24] border-white/[0.24] rounded-tl-[12px] !rounded-tr-[0] !rounded-br-[0] rounded-bl-[12px]" : "rounded-[12px]"
            }`}
          >
            {selectedIntegration === "jira" ? (
              <>
                <img src={JiraSm} /> Jira issue
              </>
            ) : selectedIntegration === "linear" ? (
              <>
                <img src={LinearSm} /> Linear issue
              </>
            ) : selectedIntegration === "slack" ? (
              <>
                <img src={SlackSm} /> Slack Message
              </>
            ) : (
              <>
                <img src={IntegrationsIcons} />
                Integrations
              </>
            )}
          </button>
          {(integrations.jira.connected || integrations.linear.connected || integrations.slack.connected) && (
            <button
              onClick={() => setShowIntegrationsDropdown((prev) => !prev)}
              className="flex items-center justify-center h-[36px] px-[10px] bg-gray-200 transition-all hover:bg-gray-300 !rounded-tl-[0] !rounded-bl-[0] rounded-tr-[12px] rounded-br-[12px]"
            >
              <img
                className={`${
                  showIntegrationsDropdown ? "rotate-[-180deg]" : ""
                } transition-all`}
                src={ChevronDownSm}
              />
            </button>
          )}
          <AnimatePresence>
            {showIntegrationsDropdown && (
              <IntegrationsList
                setShowIntegrationsDropdown={setShowIntegrationsDropdown}
                setShowJiraModal={setShowJiraModal}
                setShowLinearModal={setShowLinearModal}
                reportId={reportId as string}
              />
            )}
          </AnimatePresence>
        </div>

        {reportId && (
          <>
            <JiraIssueModal
              isOpen={showJiraModal}
              onClose={() => setShowJiraModal(false)}
              reportId={reportId}
              reproductionSteps={reproductionSteps}
            />
            <LinearIssueModal
              isOpen={showLinearModal}
              onClose={() => setShowLinearModal(false)}
              reportId={reportId}
              reproductionSteps={reproductionSteps}
            />
          </>
        )}

        <div className="flex relative z-[11]">
          <button
            onMouseEnter={() => setShowCopyTooltip(true)}
            onMouseLeave={() => setShowCopyTooltip(false)}
            onClick={() => {
              // Only include the id param in the shared URL
              const shareUrl = `${window.location.origin}${location.pathname}?id=${reportId}`;
              navigator.clipboard.writeText(shareUrl);

              showCustomToast("Link copied to clipboard", "check");
            }}
            className="border-r-[1px] border-white/[0.24] relative h-[36px] flex items-center justify-center pl-[12px] pr-[11px] rounded-tl-[12px] !rounded-tr-[0] !rounded-br-[0] rounded-bl-[12px] font-medium text-[14px] leading-[20px] bg-blue hover:bg-blue/[0.8] transition-all"
          >
            Share
          </button>

          <button
            onClick={() => {
              // Only include the id param in the shared URL
              const shareUrl = `${window.location.origin}${location.pathname}?id=${reportId}`;
              navigator.clipboard.writeText(shareUrl);

              showCustomToast("Link copied to clipboard", "check");
            }}
            onMouseEnter={() => setShowCopyTooltip(true)}
            onMouseLeave={() => setShowCopyTooltip(false)}
            className="bg-blue hover:bg-blue/[0.8] transition-all p-[10px] !rounded-tl-[0] !rounded-bl-[0] rounded-tr-[12px] rounded-br-[12px]"
          >
            <img src={Staple} className="w-4 h-4" />
          </button>
          <div
            className={`absolute z-[11] bottom-[-30px] left-1/2 -translate-x-1/2 bg-[#303030] text-white text-[12px] leading-[16px] flex items-center justify-center px-[5px] border border-white/[0.08] rounded-[8px] whitespace-nowrap pointer-events-none transition-opacity duration-[250ms] ease-in-out h-[20px] ${
              showCopyTooltip ? "opacity-100" : "opacity-0"
            }`}
          >
            Copy link
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportHeader;
